import { useEffect, useRef, useState } from 'react';
import { Button, Image, Upload, message } from 'antd';
import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import { useToggle } from 'react-use';
import { setOrderPayReceiptImages } from 'apis/oms';
import LOCALS from 'commons/locals';
import i18n from 'i18n';
import PrivateReceiptImage from 'components/private-receipt-image';
import { ALLOWED_RECEIPT_IMAGE_ACCEPT } from 'utils/receipt-image-upload';
import { uploadOrderReceiptImageFile } from 'utils/order-image-upload';
import { ReceiptImagesManageModal } from 'components/receipt-images-manage-modal';

type UpdateOrderPayReceiptImagesProps = {
  orderId: number;
  payId: string | number;
  keys: string[];
};

export const UpdateOrderPayReceiptImages = ({
  orderId,
  payId,
  keys,
}: UpdateOrderPayReceiptImagesProps) => {
  const [receiptKeys, setReceiptKeys] = useState<string[]>(keys);
  const [open, setOpen] = useState(false);
  const [uploading, toggleUploading] = useToggle(false);
  const [fileUploading, toggleFileUploading] = useToggle(false);
  const uploadingCountRef = useRef(0);

  useEffect(() => {
    setReceiptKeys(keys);
  }, [keys]);

  const handleUploadFile = async (file: File) => {
    uploadingCountRef.current += 1;
    toggleFileUploading(true);
    try {
      const key = await uploadOrderReceiptImageFile(orderId, file);
      if (key) {
        setReceiptKeys((prev) => [...prev, key]);
      }
    } finally {
      uploadingCountRef.current = Math.max(0, uploadingCountRef.current - 1);
      if (uploadingCountRef.current === 0) {
        toggleFileUploading(false);
      }
    }
  };

  const handleOk = () => {
    if (uploading || fileUploading) return;
    toggleUploading(true);
    setOrderPayReceiptImages({
      orderId,
      payId: String(payId),
      keys: receiptKeys,
    })
      .then(() => {
        message.success(i18n.t(LOCALS.successful_operation));
      })
      .finally(() => {
        toggleUploading(false);
        setOpen(false);
        window.location.reload();
      });
  };

  return (
    <>
      <ReceiptImagesManageModal
        open={open}
        receiptKeys={receiptKeys}
        onChange={setReceiptKeys}
        onClose={() => setOpen(false)}
        footer={
          <Button
            type="primary"
            loading={uploading}
            disabled={fileUploading}
            onClick={handleOk}
          >
            {i18n.t(LOCALS.confirm)}
          </Button>
        }
        extra={
          <Upload.Dragger
            accept={ALLOWED_RECEIPT_IMAGE_ACCEPT}
            multiple
            showUploadList={false}
            disabled={fileUploading || uploading}
            beforeUpload={(file) => {
              void handleUploadFile(file);
              return false;
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {i18n.t(LOCALS.payment_receipt_image_drag_hint)}
            </p>
            <p className="ant-upload-hint">
              {i18n.t(LOCALS.payment_receipt_image_invalid_type)}
            </p>
          </Upload.Dragger>
        }
      />
      <Button
        icon={<UploadOutlined />}
        title={String(i18n.t(LOCALS.payment_receipt_image_upload))}
        onClick={() => setOpen(true)}
      />
    </>
  );
};

type OrderPayReceiptImagesCellProps = {
  orderId: number;
  payId: string | number;
  receiptKeys?: string[];
};

export const OrderPayReceiptImagesCell = ({
  orderId,
  payId,
  receiptKeys = [],
}: OrderPayReceiptImagesCellProps) => (
  <div className="flex items-center gap-2">
    {receiptKeys.length > 0 ? (
      <Image.PreviewGroup>
        {receiptKeys.map((imageKey) => (
          <PrivateReceiptImage key={imageKey} imageKey={imageKey} />
        ))}
      </Image.PreviewGroup>
    ) : null}
    <UpdateOrderPayReceiptImages
      orderId={orderId}
      payId={payId}
      keys={receiptKeys}
    />
  </div>
);
